import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGeoLocation, submitLead } from '@/lib/api';
import { COUNTRIES } from '@/lib/constants';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

export default function LeadForm({ packageTitle = '', packageAmount = 0, buttonText = 'Get Started', onSuccess }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGeoLocation().then(data => {
      if (data?.isSuccess && data?.data) {
        setGeoData(data);
        const found = COUNTRIES.find(c => c.code === data.data.countryCode);
        if (found) setSelectedCountry(found.code);
      }
    }).catch(() => {});
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setForm(prev => ({ ...prev, phone: value }));
    }
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.phone) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    const country = COUNTRIES.find(c => c.code === selectedCountry);
    const payload = {
      name: form.name,
      email: form.email,
      countryDialCode: country?.dialCode || 1,
      country: selectedCountry,
      phoneNumber: form.phone,
      ipAddress: geoData?.data?.ipAddress || '',
      description: form.message,
      pageUrl: window.location.href,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: packageTitle || '',
      packageAmount: packageAmount || 0,
    };
    try {
      const result = await submitLead(payload);
      if (result.success && result.customerId) {
        if (onSuccess) onSuccess();
        navigate(`/application/main?customerid=${result.customerId}`);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="form-error">
          {error}
        </div>
      )}

      <div>
        <Input
          type="text"
          placeholder="Your Name *"
          value={form.name}
          onChange={handleChange('name')}
          className="h-12 border-zinc-300 focus:border-[#002FA7] font-body"
          data-testid="form-name-input"
          required
        />
      </div>

      <div>
        <Input
          type="email"
          placeholder="Email Address *"
          value={form.email}
          onChange={handleChange('email')}
          className="h-12 border-zinc-300 focus:border-[#002FA7] font-body"
          data-testid="form-email-input"
          required
        />
      </div>

      <div className="flex gap-2">
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="h-12 px-2 border border-zinc-300 bg-white text-sm font-body w-[120px] shrink-0 focus:border-[#002FA7] focus:outline-none"
          data-testid="form-country-select"
        >
          {COUNTRIES.map(c => (
            <option key={c.code} value={c.code}>
              {c.code} +{c.dialCode}
            </option>
          ))}
        </select>
        <Input
          type="tel"
          placeholder="Phone Number *"
          value={form.phone}
          onChange={handlePhoneChange}
          className="h-12 border-zinc-300 focus:border-[#002FA7] font-body flex-1"
          data-testid="form-phone-input"
          maxLength={10}
          inputMode="numeric"
          pattern="[0-9]*"
          required
        />
      </div>

      <div>
        <Textarea
          placeholder="Tell us about your brand or how we can help you..."
          value={form.message}
          onChange={handleChange('message')}
          className="border-zinc-300 focus:border-[#002FA7] font-body min-h-[80px]"
          data-testid="form-message-input"
          rows={3}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-6 py-3.5 font-bold text-sm tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="form-submit-btn"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {loading ? 'Submitting...' : buttonText}
      </button>
    </form>
  );
}
